/**
 * Deal form fields and their reader. See lead-form.tsx for why the two are
 * co-located and why the reader gates on `form.has()`.
 *
 * Stage is absent from both create and edit: it moves through
 * `changeDealStatusInput` so a losing stage can carry its reason in the same
 * request. `updateDealInput` omits it outright, so the contract says so too.
 */

import type { Deal } from '@erp/contracts'
import type { Contact, Organization } from '@erp/contracts'
import { decimal, int, text } from '../forms'
import { optionsFrom, useLeadSources, useOwnerOptions } from '../status-context'
import { Field, Select, TextInput } from '../ui'

export function readDealForm(form: FormData): Record<string, unknown> {
  const fields: Record<string, unknown> = {}
  const put = (key: string, value: unknown) => {
    if (form.has(key)) fields[key] = value
  }

  put('organizationId', text(form, 'organizationId'))
  put('primaryContactId', text(form, 'primaryContactId'))
  put('sourceId', text(form, 'sourceId'))
  put('ownerId', text(form, 'ownerId'))
  put('amount', decimal(form, 'amount'))
  put('currency', text(form, 'currency'))
  put('probability', int(form, 'probability'))
  put('expectedCloseDate', text(form, 'expectedCloseDate'))

  return fields
}

export function DealFields({
  deal,
  organizations,
  contacts,
  presetOrganizationId,
  issues,
}: {
  deal?: Deal | undefined
  organizations: readonly Organization[]
  contacts: readonly Contact[]
  presetOrganizationId?: string | null
  issues: Record<string, string[]>
}) {
  const sources = useLeadSources()
  const owners = useOwnerOptions(deal?.ownerId ?? null)

  const selectedOrganization = deal?.organizationId ?? presetOrganizationId ?? ''

  /*
   * Contacts are labelled with their organization rather than filtered by it:
   * filtering as the organization select changes would need either a round trip
   * per change or a client-side store, and the label disambiguates well enough
   * at this scale. Revisit alongside the combobox, when both lists outgrow a
   * plain <select>.
   */
  const contactOptions = [
    { value: '', label: 'None' },
    ...contacts.map((contact) => {
      const name = [contact.firstName, contact.lastName].filter(Boolean).join(' ')
      const org = organizations.find((o) => o.id === contact.organizationId)?.name
      const label = name.length > 0 ? name : (contact.email ?? 'Unnamed contact')
      return { value: contact.id, label: org ? `${label} — ${org}` : label }
    }),
  ]

  return (
    <>
      <fieldset className="ax-fieldset">
        <legend>Customer</legend>
        <Field label="Organization" error={issues['organizationId']}>
          <Select
            name="organizationId"
            value={selectedOrganization}
            options={optionsFrom(organizations)}
          />
        </Field>
        <Field label="Primary contact" error={issues['primaryContactId']}>
          <Select
            name="primaryContactId"
            value={deal?.primaryContactId ?? ''}
            options={contactOptions}
          />
        </Field>
      </fieldset>

      <fieldset className="ax-fieldset">
        <legend>Commercials</legend>
        <Field label="Amount" error={issues['amount']}>
          <TextInput name="amount" type="number" defaultValue={deal?.amount?.toString()} />
        </Field>
        {/* Required whenever an amount is set — a database constraint, mirrored here. */}
        <Field label="Currency" error={issues['currency']}>
          <TextInput name="currency" placeholder="USD" defaultValue={deal?.currency} />
        </Field>
        <Field label="Probability (%)" error={issues['probability']}>
          <TextInput name="probability" type="number" defaultValue={deal?.probability?.toString()} />
        </Field>
        <Field label="Expected close" error={issues['expectedCloseDate']}>
          <TextInput
            name="expectedCloseDate"
            type="date"
            defaultValue={deal?.expectedCloseDate ?? ''}
          />
        </Field>
      </fieldset>

      <fieldset className="ax-fieldset">
        <legend>Ownership</legend>
        <Field label="Source" error={issues['sourceId']}>
          <Select name="sourceId" value={deal?.sourceId ?? ''} options={optionsFrom(sources)} />
        </Field>
        <Field label="Owner" error={issues['ownerId']}>
          <Select name="ownerId" value={deal?.ownerId ?? ''} options={owners} />
        </Field>
      </fieldset>
    </>
  )
}
